import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Text,
  Box,
  Container,
  Heading,
  Image,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { validateSerialNumberForProduct } from '../../utils/serialValidation';

const ProductRegistration = forwardRef(({ product, onSubmit }, ref) => {
  const [purchaseDate, setPurchaseDate] = useState(null);
  const [serialNumber, setSerialNumber] = useState(product?.serialPrefix || '');
  const [errors, setErrors] = useState({});
  
  // Get current date for registration date display
  const registrationDate = new Date().toLocaleDateString('en-GB');

  const validateForm = () => {
    const newErrors = {};
    
    if (!purchaseDate) {
      newErrors.purchaseDate = 'Purchase date is required';
    }

    // Only validate serial number if serialPlaceholder exists
    if (product?.serialPlaceholder) {
      const serialValidation = validateSerialNumberForProduct(serialNumber, product.type);
      if (!serialValidation.isValid) {
        newErrors.serialNumber = serialValidation.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (validateForm()) {
        onSubmit({
          purchaseDate,
          serialNumber: product?.serialPlaceholder ? serialNumber : undefined,
          registrationDate: new Date(),
        });
        return true;
      }
      return false;
    }
  }));

  // Handle serial number input formatting
  const handleSerialNumberChange = (e) => {
    let value = e.target.value.toUpperCase();

    // Add hyphens automatically
    if (value.length > 6 && !value.includes('-', 6)) {
      value = value.slice(0, 6) + '-' + value.slice(6);
    }
    if (value.length > 10 && !value.includes('-', 10)) {
      value = value.slice(0, 10) + '-' + value.slice(10);
    }
    if (value.length > 14 && !value.includes('-', 14)) {
      value = value.slice(0, 14) + '-' + value.slice(14);
    }
    
    setSerialNumber(value);
  };

  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg" textAlign="center">Product Registration</Heading>

        {/* Product Information Section */}
        <HStack spacing={8} align="flex-start">
          <Box flex="1" maxW="200px">
            <Image
              src={product.image}
              alt={product.name}
              width="100%"
              height="auto"
              objectFit="contain"
              fallbackSrc="https://via.placeholder.com/200"
            />
          </Box>
          <VStack flex="2" align="stretch" spacing={2}>
            <Text fontSize="xl" fontWeight="bold">{product.name}</Text>
            <Text fontSize="md" color="gray.600">Category: {product.type}</Text>
            <Text fontSize="sm" color="gray.500">Product SKU: {product.id}</Text>
          </VStack>
        </HStack>

        <Divider />
        
        {/* Registration Form Section */}
        <VStack spacing={6} align="stretch">
          <FormControl isInvalid={!!errors.purchaseDate}>
            <FormLabel fontSize="xl">Purchase Date</FormLabel>
            <Box position="relative">
              <DatePicker
                selected={purchaseDate}
                onChange={setPurchaseDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                maxDate={new Date()}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="chakra-input"
                customInput={
                  <Input />
                }
              />
              <CalendarIcon
                position="absolute"
                right="8px"
                top="50%"
                transform="translateY(-50%)"
                color="gray.400"
                pointerEvents="none"
              />
            </Box>
            {errors.purchaseDate && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {errors.purchaseDate}
              </Text>
            )}
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xl">Registration Date</FormLabel>
            <Text fontSize="md">{registrationDate}</Text>
          </FormControl>

          {product?.serialPlaceholder && (
            <FormControl isInvalid={!!errors.serialNumber}>
              <FormLabel fontSize="xl">Device Serial</FormLabel>
              <Input
                value={serialNumber}
                onChange={handleSerialNumberChange}
                placeholder={product.serialPlaceholder}
                size="md"
                maxLength={19}
              />
              {errors.serialNumber && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {errors.serialNumber}
                </Text>
              )}
            </FormControl>
          )}
        </VStack>
      </VStack>
    </Container>
  );
});

ProductRegistration.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    serialPrefix: PropTypes.string,
    serialPlaceholder: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
};

ProductRegistration.defaultProps = {
  product: {
    serialPrefix: '',
  },
};

export default ProductRegistration; 